import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiClient } from '@/shared/api/client'
import { endpoints } from '@/shared/api/endpoints'
import { useAppStore } from '@/shared/stores/appStore'
import type {
  CreateSurvivorGameInput,
  CreateSurvivorGameResult,
  JoinSurvivorGameResult,
  RegenerateSurvivorInviteCodeResult,
  SubmitSurvivorPickInput,
  SubmitSurvivorPickResult,
  SurvivorAvailableTeam,
  SurvivorGameDetail,
  SurvivorGameListItem,
  SurvivorPeerPick,
  SurvivorPickHistoryEntry,
  SurvivorSeason,
  SurvivorStandings,
  RestartSurvivorGameResult,
  UpdateSurvivorGameInput,
} from './types'

// ── Query key factory ─────────────────────────────────────────────────────────

export const survivorKeys = {
  all:           ['survivor'] as const,
  list:          ()                            => [...survivorKeys.all, 'list']                            as const,
  detail:        (gameId: string)              => [...survivorKeys.all, gameId]                            as const,
  standings:     (gameId: string)              => [...survivorKeys.detail(gameId), 'standings']            as const,
  myPicks:       (gameId: string)              => [...survivorKeys.detail(gameId), 'my-picks']             as const,
  picksGw:       (gameId: string, gwId: string)=> [...survivorKeys.detail(gameId), 'picks', gwId]          as const,
  availableTeams:(gameId: string, gwId: string)=> [...survivorKeys.detail(gameId), 'available', gwId]      as const,
  hallOfFame:    (gameId: string)              => [...survivorKeys.detail(gameId), 'hall-of-fame']         as const,
}

// ── Reads ─────────────────────────────────────────────────────────────────────

// ── Competition gameweeks (drives the GW navigator) ──────────────────────────

export type CompetitionGameweek = {
  id:                     string
  number:                 number
  phase:                  string
  status:                 'UPCOMING' | 'ACTIVE' | 'LOCKED' | 'FINALIZED'
  firstMatchAt:           string
  lastMatchEndsAt:        string
  canHaveExtraTime:       boolean
  scoresFinalizedAt:      string | null
  transferWindowOpensAt:  string | null
}

export function useCompetitionGameweeks(competitionId: string | undefined) {
  return useQuery({
    queryKey: ['competition', competitionId, 'gameweeks'] as const,
    queryFn:  () => apiClient.get<CompetitionGameweek[]>(
      `/v1/gameweeks?competitionId=${competitionId}`,
    ),
    enabled:   !!competitionId,
    staleTime: 60_000,
  })
}

export async function fetchUserSurvivorGames(): Promise<SurvivorGameListItem[]> {
  return apiClient.get<SurvivorGameListItem[]>(endpoints.survivorGames.list())
}

export function useUserSurvivorGames() {
  return useQuery({
    queryKey: survivorKeys.list(),
    queryFn:  fetchUserSurvivorGames,
    staleTime: 60_000,
  })
}

export async function fetchSurvivorGame(gameId: string): Promise<SurvivorGameDetail> {
  return apiClient.get<SurvivorGameDetail>(endpoints.survivorGames.detail(gameId))
}

export function useSurvivorGame(gameId: string) {
  return useQuery({
    queryKey: survivorKeys.detail(gameId),
    queryFn:  () => fetchSurvivorGame(gameId),
    staleTime: 30_000,
  })
}

export function useSurvivorStandings(gameId: string) {
  return useQuery({
    queryKey: survivorKeys.standings(gameId),
    queryFn:  () => apiClient.get<SurvivorStandings>(endpoints.survivorGames.standings(gameId)),
    staleTime: 30_000,
  })
}

export function useMySurvivorPicks(gameId: string) {
  return useQuery({
    queryKey: survivorKeys.myPicks(gameId),
    queryFn:  () => apiClient.get<SurvivorPickHistoryEntry[]>(endpoints.survivorGames.myPicks(gameId)),
    staleTime: 30_000,
  })
}

export function useSurvivorPicks(gameId: string, gameweekId: string | undefined) {
  return useQuery({
    queryKey: survivorKeys.picksGw(gameId, gameweekId ?? 'none'),
    queryFn:  () => apiClient.get<SurvivorPeerPick[]>(
      endpoints.survivorGames.picksByGw(gameId, gameweekId!),
    ),
    enabled:   !!gameweekId,
    staleTime: 30_000,
  })
}

// Every season of this game's series, oldest first. Cheap and stable — a season only changes
// when one ends or a new one starts — so it is cached for longer than the live reads.
export function useSurvivorHallOfFame(gameId: string, enabled = true) {
  return useQuery({
    queryKey: survivorKeys.hallOfFame(gameId),
    queryFn:  () => apiClient.get<SurvivorSeason[]>(endpoints.survivorGames.hallOfFame(gameId)),
    enabled,
    staleTime: 300_000,
  })
}

export function useSurvivorAvailableTeams(gameId: string, gameweekId: string | undefined) {
  return useQuery({
    queryKey: survivorKeys.availableTeams(gameId, gameweekId ?? 'none'),
    queryFn:  () => apiClient.get<SurvivorAvailableTeam[]>(
      endpoints.survivorGames.availableTeams(gameId, gameweekId!),
    ),
    enabled:   !!gameweekId,
    staleTime: 30_000,
  })
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateSurvivorGame() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateSurvivorGameInput) =>
      apiClient.post<CreateSurvivorGameResult>(endpoints.survivorGames.create(), input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: survivorKeys.list() }),
  })
}

export function useJoinSurvivorGame() {
  const queryClient = useQueryClient()
  const addToast    = useAppStore((s) => s.addToast)
  return useMutation({
    mutationFn: (code: string) =>
      apiClient.post<JoinSurvivorGameResult>(endpoints.survivorParticipations.join(), { code }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: survivorKeys.list() }),
    onError: (error) => {
      if (error instanceof ApiError) return
      addToast({ type: 'error', title: 'Não foi possível entrar no jogo.' })
    },
  })
}

export function useSubmitSurvivorPick(gameId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SubmitSurvivorPickInput) =>
      apiClient.post<SubmitSurvivorPickResult>(endpoints.survivorGames.submitPick(gameId), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: survivorKeys.detail(gameId) })
    },
  })
}

export function useLeaveSurvivorGame() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (participationId: string) =>
      apiClient.delete<void>(endpoints.survivorParticipations.leave(participationId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: survivorKeys.list() }),
  })
}

export function useDeleteSurvivorGame() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (gameId: string) =>
      apiClient.delete<void>(endpoints.survivorGames.delete(gameId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: survivorKeys.list() }),
  })
}

export function useUpdateSurvivorGame(gameId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateSurvivorGameInput) =>
      apiClient.patch<void>(endpoints.survivorGames.update(gameId), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: survivorKeys.detail(gameId) })
      void queryClient.invalidateQueries({ queryKey: survivorKeys.list() })
    },
  })
}

export function useUpdateSurvivorTeamName(gameId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    // null/empty clears the alias (falls back to the username).
    mutationFn: (teamName: string | null) =>
      apiClient.patch<void>(endpoints.survivorGames.updateTeamName(gameId), { teamName }),
    onSuccess: () => {
      // detail() is the prefix for standings/picks too — invalidating it refreshes the
      // alias everywhere it surfaces (header, standings, peer picks).
      void queryClient.invalidateQueries({ queryKey: survivorKeys.detail(gameId) })
    },
  })
}

// Starts the next season of a finished game. The response carries the NEW game id — the caller
// has to navigate there, since the season the user is looking at stays put as the record.
export function useRestartSurvivorGame(gameId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post<RestartSurvivorGameResult>(endpoints.survivorGames.restart(gameId), {}),
    onSuccess: () => {
      // The finished season now links forward and loses its restart affordance, and the list
      // has to swap this card for the new season's.
      void queryClient.invalidateQueries({ queryKey: survivorKeys.detail(gameId) })
      void queryClient.invalidateQueries({ queryKey: survivorKeys.list() })
    },
  })
}

export function useRegenerateSurvivorInviteCode(gameId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post<RegenerateSurvivorInviteCodeResult>(
        endpoints.survivorGames.regenerateCode(gameId), {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: survivorKeys.detail(gameId) })
      void queryClient.invalidateQueries({ queryKey: survivorKeys.list() })
    },
  })
}
